import { axiosInstance } from "./axios";
import { z } from "zod";

const kpisSchema = z.object({
    id: z.number(),
    teamId: z.number().optional(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.array(z.string()),
    averageKpi: z.number(),
    bestKpi: z.number(),
    lastOpponent: z.string(),
    lastDate: z.string(),
});

const playerListSchema = z.object({
    tournamentId: z.number(),
    goalkeepersKpis: z.array(kpisSchema),
    kpis: z.array(kpisSchema),
});

export type Player = z.infer<typeof kpisSchema>;
export type PlayerList = z.infer<typeof playerListSchema>;

const fetchPlayerList = async (tournamentId?: number, teamId?: number): Promise<PlayerList> => {
    try {
        const tid = tournamentId ?? 0;
        const gid = teamId ?? 0;
        const response = await axiosInstance.get(`/${tid}/${gid}/player-list`);
        return playerListSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch player list data: ${error}`);
    }
};

export const playersListQueryOptions = (tournamentId?: number, teamId?: number) => ({
    queryKey: ["player-list", tournamentId, teamId],
    queryFn: () => fetchPlayerList(tournamentId, teamId),
    staleTime: 5 * 60 * 1000,
});

const fetchTeamPlayerList = async (tournamentId: number, teamId: number): Promise<PlayerList> => {
    try {
        const response = await axiosInstance.get(`/${tournamentId}/${teamId}/player-list`);
        return playerListSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch team player list: ${error}`);
    }
};

export const teamPlayerListQueryOptions = (tournamentId: number, teamId: number) => ({
    queryKey: ["team-player-list", tournamentId, teamId],
    queryFn: () => fetchTeamPlayerList(tournamentId, teamId),
    enabled: !!tournamentId && !!teamId,
    staleTime: 5 * 60 * 1000,
});
